import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Input, Typography, Button } from '../../../Material-UI/Components';
import HorizontalLoader from '../../../Material-UI/Components/Progress/LinearProgress/LinearProgress';
import APIService from '../../../service/APIService';
import { useAppContext } from '../../../store/context';
import NavBar from '../../../Components/NavBar/NavBar';
import { useLocation } from 'react-router-dom';
import { ProSidebarProvider } from 'react-pro-sidebar';
import AuthShell from '../AuthShell';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  actions: {
    marginTop: 18,
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  input: {
    width: '100%',
    margin: '16px 0 0 !important',
    background: '#ffffff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 18,
      background: '#f8fbff',
      minHeight: 56,
    },
  },
  primaryAction: {
    minHeight: 52,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)',
    boxShadow: '0 16px 28px rgba(14, 165, 233, 0.28)',
    color: '#ffffff',
    fontWeight: 700,
    letterSpacing: '0.08em',
    '&:hover': {
      background: 'linear-gradient(135deg, #075985 0%, #0284c7 100%)',
    },
  },
  secondaryAction: {
    minHeight: 48,
    borderRadius: 16,
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    color: '#0f172a',
    fontWeight: 700,
  },
  infoCard: {
    marginTop: 18,
    borderRadius: 18,
    border: '1px solid rgba(14, 165, 233, 0.18)',
    background: '#f0f9ff',
    padding: '14px 16px',
  },
  infoLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#0ea5e9',
  },
  infoText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 1.7,
    color: '#334155',
  },
  status: {
    marginTop: 18,
    borderRadius: 18,
    padding: '12px 14px',
    fontSize: 14,
    lineHeight: 1.6,
  },
  statusSuccess: {
    border: '1px solid #bbf7d0',
    background: '#f0fdf4',
    color: '#166534',
  },
  statusError: {
    border: '1px solid #fecaca',
    background: '#fff1f2',
    color: '#b91c1c',
  },
}));

const VerifyEmail = ({ email }) => {
  const classes = useStyles();
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [token, setToken] = useState('');

  const sendMail = () => {
    APIService.resendVerificationMail()
      .then((resp) => {
        const { message } = resp.data;
        setStatusType('success');
        setStatus(message || 'Verification email sent.');
      })
      .catch(() => {
        setStatusType('error');
        setStatus('We could not resend the verification email right now. Please try again in a moment.');
      });
  };

  const logout = () => {
    APIService.logout()
      .then(() => {
        document.location = '/track/login';
      });
  };

  const verifyNow = () => {
    if (token) {
      document.location = '/bapp/auth/verify/' + token.trim();
    }
  };

  return (
    <AuthShell
      eyebrow="Career Operating System"
      heroTitle="Verify your email to unlock your workspace."
      heroSubtitle="Your account is created. One quick verification step connects your saved progress, planning, and next actions to this email securely."
      heroBadges={[
        {
          label: 'Current step',
          value: 'Confirm email ownership',
          hint: 'Use the one-time code from your inbox to finish setup.',
        },
        {
          label: 'After verification',
          value: 'Resume full workspace access',
          hint: 'Your strategy, execution, and interview prep will all be available in one place.',
        },
      ]}
      heroQuoteTitle="Why this matters"
      heroQuoteBody="Verification protects your workspace and makes sure important planning updates and career actions reach the right inbox."
      formEyebrow="Finish setup"
      formTitle="Enter your verification code"
      formSubtitle={`We sent a one-time code to ${email || 'your email address'}. Enter it below, or resend the email if you need a fresh one.`}
      footerPrompt="Already have the email and want to sign in again?"
      footerActionLabel="Go to login"
      onFooterAction={logout}
    >
      <div className={classes.infoCard}>
        <div className={classes.infoLabel}>Verification note</div>
        <div className={classes.infoText}>
          The message comes from <strong>app.team@myjobaide.com</strong>. If you do not see it right away, check spam or promotions before resending.
        </div>
      </div>

      <Input
        className={classes.input}
        type="text"
        label="Verification code"
        onChange={(e) => setToken(e.target.value)}
        value={token}
        required
        fullWidth
      />

      <div className={classes.actions}>
        <Button className={classes.primaryAction} fullWidth onClick={verifyNow}>
          Verify email
        </Button>
      </div>

      <div className={classes.actions}>
        <Button className={classes.secondaryAction} onClick={sendMail}>
          Resend email
        </Button>
        <Button className={classes.secondaryAction} onClick={logout}>
          Back to login
        </Button>
      </div>

      {status ? (
        <div className={`${classes.status} ${statusType === 'error' ? classes.statusError : classes.statusSuccess}`}>
          {status}
        </div>
      ) : null}
    </AuthShell>
  );
};

const VerifiedRoute = (props) => {
  const classes = useStyles();
  const { state } = useAppContext();
  const { email, confirmed, premium } = state.user;
  const { pathname: currentPath } = useLocation();
  const { children } = props;
  const useLegacyShell = props.useLegacyShell !== false;
  const loadShop = (event) => {
    event.preventDefault();
    window.document.location.href = "/track/shopanalyze";
  };
  return (
    <div className={classes.root}>
      {email
        ? (
          <div>
            {useLegacyShell && !(premium || currentPath.startsWith("/shopanalyze")) ? <div style={{ display: 'flex', background: 'linear-gradient(45deg, #669999 30%, #009999 90%)', border: 0, borderRadius: 3, boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)', color: 'white', height: 48, padding: '0 30px', justifyContent: 'center', width: '100%' }}><Button onClick={(event) => loadShop(event)}>Subscribe to Premium <img height="44px" src="premium.png" alt="premium" /></Button></div> : ""}
            {confirmed ? (
              useLegacyShell ? (
                <ProSidebarProvider>
                  <NavBar>
                    {' '}
                    {children}
                    {' '}
                  </NavBar>
                </ProSidebarProvider>
              ) : children
            ) : <VerifyEmail email={email} />}
          </div>
        ) : <HorizontalLoader />}
    </div>
  );
};

export default VerifiedRoute;
